import React, {useEffect, useState} from 'react';
import {t} from 'i18next';
import {useSelector} from 'react-redux';
import {ReactComponent as ExportIcon} from '../../../../src/assests/svg/export_icon.svg';
import {getExportReportXlsx} from '../../../api/ReportingManagement';
import {
  GA_CLICK_TYPES,
  GA_DATA_MODE_AUTO,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_REPORT_TYPE,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {RootState} from '../../../redux/store';
import {
  downloadResponseAsXLSX,
  getFilenameFromHeaders,
} from '../../../utils/utils';
import AurButton from '../../common/aur-button/AurButton';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurTabMenu, {AurTab} from '../../common/aur-tab-menu/AurTabMenu';
import {ReportScreenTabTypes} from '../../pages/reports/ProjectOverview';
import ProcessingExceptions from './ProcessingExceptionsTab/ProcessingExceptions';
import {getReportDataByType} from './ProcessingExceptionsTab/utils';
import ProcessingSummaryTab from './ProcessingSummaryTab/ProcessingSummaryTab';
import {TABLE_PER_PAGE_5000_ROW} from '../../../constants/constants';

interface Props {
  openSpecificTab?: (type: ReportScreenTabTypes) => void;
  canViewSummary: boolean;
  canViewExceptions: boolean;
}

const ProcessingSummaryAndExceptionsDetails: React.FC<Props> = ({
  openSpecificTab,
  canViewExceptions,
  canViewSummary,
}) => {
  const {clientId, projectId, workspaceId, processingTab} = useSelector(
    (state: RootState) => state.reports
  );

  const {trackGAEvent} = useAnalytics();

  const [selectedTab, setSelectedTab] = useState<number>(processingTab ?? 0);
  const [activeReport, setActiveReport] = useState('');
  const [loading] = useState(false);
  const [isClearAllLoading, setIsClearAllLoading] = useState<boolean>(false);
  const [skipTabTracking, setSkipTabTracking] = useState(true);

  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.PAGE_VIEW,
      page: GA_DATA_MODE_AUTO,
      user: GA_DATA_MODE_AUTO,
      report: {
        name: t('gaTracking.reports.tabProcessingSummary'),
        type: GA_REPORT_TYPE.STANDARD,
        load_time: '0',
      },
    });
  }, []);

  const handleDownloadProcessingExceptionsXlsx = async () => {
    setIsClearAllLoading(true);
    try {
      const response = await getExportReportXlsx(
        clientId as string,
        projectId as string,
        workspaceId as string,
        '/processing-exceptions',
        dataSetName as string,
        getReportDataByType(activeReport).type,
        1,
        TABLE_PER_PAGE_5000_ROW
      );
      const fileName =
        getFilenameFromHeaders(response.headers['content-disposition']) ??
        `${workspaceId}_${Date.now()}`;
      downloadResponseAsXLSX(response, fileName, false);
    } catch (e) {
      console.log(e);
    } finally {
      setIsClearAllLoading(false);
    }
  };

  const getTabs = () => {
    let index = 0;
    const newTabs: AurTab[] = [];
    if (canViewSummary) {
      newTabs.push({
        header: {
          text: t('reportTabs.processingSummary'),
        },
        content: (
          <ProcessingSummaryTab
            openSpecificTab={openSpecificTab}
            skipTabTracking={skipTabTracking}
          />
        ),
        index: index,
      });
      index++;
    }

    if (canViewExceptions) {
      newTabs.push({
        header: {
          text: t('reportTabs.processingExceptions'),
        },
        content: (
          <ProcessingExceptions
            setActiveReport={setActiveReport}
            skipTabTracking={skipTabTracking}
          />
        ),
        index: index,
      });
      index++;
    }
    return newTabs;
  };

  const tabs: AurTab[] = getTabs();

  const dataSetName = useSelector(
    (state: RootState) => state.reports.workspaceDataSetName
  );

  return (
    <>
      <div className="processing-summary-and-exceptions-details">
        <AurTabMenu
          headerClasses="typo-text subhead1_size tab-menu-header"
          tabs={tabs}
          onTabChange={(tabIndex: number) => {
            setSelectedTab(tabIndex);
            setSkipTabTracking(false);
          }}
          width={'800px'}
          defaultTab={processingTab ?? 0}
        ></AurTabMenu>
        {selectedTab == 1 ? (
          <AurButton
            startIcon={
              isClearAllLoading ? (
                <AurLoadingIndicator
                  size={16}
                  className="progress-icon"
                  aria-hidden="true"
                />
              ) : (
                <ExportIcon aria-hidden="true" />
              )
            }
            type="continue"
            size="Medium"
            label={t('reports.processingSummary.exportAsXlsx')}
            onClick={() => {
              handleDownloadProcessingExceptionsXlsx();
              trackGAEvent({
                eventName: GA_EVENT_NAMES.EXPORT,
                click: {
                  name: t('gaTracking.reports.exportOption.xlsx'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.BODY,
                  action: t('gaTracking.reports.exportReport'),
                },
                file_extension: GA_FILE_EXTENTIONS.XLSX,
                ...(!skipTabTracking && {
                  subPage: {
                    subpage_name: t('gaTracking.reports.processingExceptions'),
                    subpage_type: GA_SUBPAGE_TYPE.TAB,
                  },
                }),
              });
            }}
            dataTestId="export-xlsx"
            classNames="typo-text buttonMedium_size export-btn"
          />
        ) : null}
      </div>
      {<AurLoadingIndicator overlay={true} isOpen={loading} />}
    </>
  );
};

export default ProcessingSummaryAndExceptionsDetails;
